import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Phone } from "lucide-react";
import { SERVICES, BRAND, GALLERY } from "@/data/content";
import Seo from "@/components/site/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const others = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <div data-testid="service-detail-page">
      <Seo
        title={`${service.title} — Tampa Bay`}
        description={`${service.overview} Call ${BRAND.phone} for a free estimate from ProCraft Solutions.`}
        path={`/services/${service.slug}`}
        image={service.image}
      />
      {/* Hero */}
      <section className="relative bg-paper pt-12 md:pt-20 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-[#16a34a]/10 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 relative">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#57534e] hover:text-[#16a34a] transition"
            data-testid="service-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mt-6">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
                ProCraft Service
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1c1917] mt-3 tracking-tighter leading-[1]"
                data-testid="service-title"
              >
                {service.title}
              </motion.h1>
              <p className="mt-5 text-lg md:text-xl text-[#3c4043] max-w-2xl leading-relaxed">
                {service.overview}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  href={BRAND.phoneHref}
                  data-testid="service-phone-cta"
                  className="inline-flex items-center justify-center gap-3 h-13 px-6 py-4 rounded-full bg-[#16a34a] hover:bg-[#15803d] text-white font-bold transition shadow-[0_10px_30px_-10px_rgba(22,163,74,0.6)]"
                >
                  <Phone className="w-5 h-5" />
                  Call {BRAND.phone}
                </a>
                <Link
                  to="/#contact"
                  data-testid="service-contact-cta"
                  className="inline-flex items-center justify-center gap-3 h-13 px-6 py-4 rounded-full bg-[#1c1917] hover:bg-black text-white font-bold transition"
                >
                  Get a free estimate
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#e7e5df] shadow-xl">
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-[#e7e5df]">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#16a34a]">
                    On-time. On-budget.
                  </div>
                  <div className="font-display font-extrabold text-xl text-[#1c1917] mt-1">
                    {service.short}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="bg-paper-alt py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
                What&rsquo;s Included
              </div>
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1c1917] mt-2 tracking-tight">
                Everything we handle, end-to-end.
              </h2>
              <p className="mt-5 text-[#57534e] text-base md:text-lg leading-relaxed">
                We bring the equipment, the team, and the experience. You get
                the result.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {service.includes.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 bg-white border border-[#e7e5df] rounded-2xl p-5"
                    data-testid="service-include-item"
                  >
                    <span className="h-8 w-8 rounded-full bg-[#16a34a]/10 text-[#16a34a] inline-flex items-center justify-center flex-none">
                      <Check className="w-4 h-4" />
                    </span>
                    <span className="text-[#1c1917] font-semibold">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Recent work */}
      <section className="bg-paper py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
            Recent Work
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1c1917] mt-2 tracking-tight">
            From the field.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {GALLERY.slice(0, 3).map((g) => (
              <div
                key={g.src}
                className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#e7e5df] bg-[#f4f1ea]"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-paper-alt py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
            FAQs
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1c1917] mt-2 tracking-tight">
            Good questions. Real answers.
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {service.faq.map((q, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white border border-[#e7e5df] rounded-2xl mb-3 px-5"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-display font-bold text-left text-lg text-[#1c1917] hover:no-underline">
                  {q.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3c4043] text-base leading-relaxed">
                  {q.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1c1917] text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            Ready to get started?
          </h2>
          <p className="mt-5 text-white/75 text-lg max-w-2xl mx-auto">
            Free estimates. Honest pricing. Real craftsmanship. Let&rsquo;s build smart from the start.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <a
              href={BRAND.phoneHref}
              data-testid="service-cta-phone"
              className="inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#16a34a] hover:bg-[#15803d] text-white font-bold transition"
            >
              <Phone className="w-5 h-5" />
              Call {BRAND.phone}
            </a>
            <Link
              to="/#contact"
              data-testid="service-cta-contact"
              className="inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#eab308] hover:bg-[#ca8a04] text-[#1c1917] font-bold transition"
            >
              Get a free estimate
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="bg-paper py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-[#1c1917] tracking-tight">
            Other ProCraft services
          </h3>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group bg-white border border-[#e7e5df] rounded-3xl overflow-hidden hover:-translate-y-1 hover:border-[#16a34a] hover:shadow-lg transition-all"
                data-testid={`other-service-${s.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="font-display font-bold text-lg text-[#1c1917]">
                    {s.title}
                  </div>
                  <div className="text-sm text-[#57534e] mt-1">{s.short}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
